import { useBunja } from 'bunja/react';
import { isEmpty } from 'es-toolkit/compat';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { OrderTypeEnum } from '@flipster/apis/types';
import { useAbFlag } from '@flipster/features/src/shared/ab-test';
import { spotOrdersBunja } from '@flipster/r230522/original/spot-order';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { Col, Table, TableHeader } from '@flipster/ui/Table';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import PendingPositionRow from './PendingPositionRow';
import styles from './positionTable.module.scss';
import { pendingPositionTableBunja } from './state';

interface Props {
  showAllSymbol: boolean;
}

const PendingPositionTable = ({ showAllSymbol }: Props) => {
  const t = useTranslations();
  const transAssets = useTranslations('Assets.r250401');
  const transTrade = useTranslations('Trade.r250401');
  const transTradeLimit = useTranslations('Trade.r251104');
  const isSpotLimitOrderEnabled = useAbFlag('25q4-spot-limit-order', 'on');
  const { pendingPositionTypeAtom } = useBunja(pendingPositionTableBunja);

  const { symbol } = useBunja(tradeSymbolBunja);
  const { pendingPositionsAtom } = useBunja(spotOrdersBunja);
  const orders = useAtomValue(pendingPositionsAtom);

  const [selectedOrderType, setSelectedOrderType] = useAtom(pendingPositionTypeAtom);

  const currentShowOrders = showAllSymbol ? orders : orders?.[symbol];

  if (isEmpty(currentShowOrders))
    return (
      <p className={styles.noData}>
        {isSpotLimitOrderEnabled
          ? transTradeLimit('When_place_limit_trigger_order_still_awaiting_show')
          : transAssets('When_you_place_a_trigger_order_orders_that_notice')}
      </p>
    );

  const currentPositions = Object.values(currentShowOrders).flatMap((item) => item);
  const positions = isSpotLimitOrderEnabled
    ? currentPositions.filter((item) =>
        selectedOrderType === 'limit' ? item.orderType === OrderTypeEnum.Limit : item.orderType !== OrderTypeEnum.Limit,
      )
    : currentPositions.flatMap((item) => item);

  const pendingPositionCount = {
    limit: currentPositions.filter((p) => p.orderType === OrderTypeEnum.Limit).length,
    trigger: currentPositions.filter((p) => p.orderType !== OrderTypeEnum.Limit).length,
  };

  const columns: Col[] = [
    { key: 'pair', title: transTrade('Spot_usdt_pairs'), width: 90, minWidth: 90 },
    { key: 'side', title: transAssets('Side'), width: 75, minWidth: 75 },
    { key: 'amount', title: transTrade('Amount'), width: 130, minWidth: 130 },
    {
      key: 'price',
      title:
        isSpotLimitOrderEnabled && selectedOrderType === 'limit'
          ? transTradeLimit('Limit_price')
          : transTrade('Trigger_price'),
      width: 90,
      minWidth: 90,
    },
    { key: 'cancel', title: '', width: 78, minWidth: 78 }, // Cancel Button 이름으로 title을 대체함으로 title을 공백으로 둠
  ];

  return (
    <>
      {isSpotLimitOrderEnabled && (
        <div className={flex({ gap: 20, textStyle: 'Text/L/Regular', pt: 8, pb: 4 })}>
          <RadioButton
            name='position-tab'
            value='limit'
            checked={selectedOrderType === 'limit'}
            onChange={() => setSelectedOrderType('limit')}
          >
            <span
              className={css({
                color: selectedOrderType === 'limit' ? 'content.default.level-5' : 'content.default.level-1',
              })}
            >
              {t('Trade.r251104.Limit')} ({pendingPositionCount.limit})
            </span>
          </RadioButton>
          <RadioButton
            name='position-tab'
            value='market'
            checked={selectedOrderType === 'trigger'}
            onChange={() => setSelectedOrderType('trigger')}
          >
            <span
              className={css({
                color: selectedOrderType === 'trigger' ? 'content.default.level-5' : 'content.default.level-1',
              })}
            >
              {t('Trade.r251008.Trigger')} ({pendingPositionCount.trigger})
            </span>
          </RadioButton>
        </div>
      )}
      <Table
        className={styles.table}
        tHeader={<TableHeader columns={columns} />}
        items={positions}
        renderRow={(position, index) => (
          <PendingPositionRow key={`${position.symbol}_${position.orderId}`} item={position} index={index} />
        )}
        withHover
      />
    </>
  );
};

export default PendingPositionTable;
